'use client';

import { FilePathUtils } from '@/config';
import { Link } from '@/i18n/routing';
import { VashaUtils } from '@/utils/vasha-utils';
import {
  CallIcon,
  Facebook01Icon,
  InstagramIcon,
  Location05Icon,
  MailOpen02Icon,
  TwitterIcon,
  WhatsappIcon,
  YoutubeIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { Logo } from '../logo';
import SupportActions from '../whatsapp-chat-support/support-action';
import DemoFooter from './demo-footer';

export const socialIconToMap = {
  facebook: <HugeiconsIcon icon={Facebook01Icon} />,
  whatsapp: <HugeiconsIcon icon={WhatsappIcon} />,
  youtube: <HugeiconsIcon icon={YoutubeIcon} />,
  twitter: <HugeiconsIcon icon={TwitterIcon} />,
  instagram: <HugeiconsIcon icon={InstagramIcon} />,
};

type FooterContentProps = {
  homepage: any;
  customer: any;
};

export default function FooterContent({
  homepage,
  customer,
}: FooterContentProps) {
  const pathname = usePathname();
  const isMyClass = pathname.includes('my-classes');
  const isMyWebinar = pathname.includes('my-webinars');
  const isIeltsMocks = pathname.includes('ielts-mock');

  if (isMyClass || isMyWebinar) {
    return null;
  }

  if (isIeltsMocks) return null;

  if (!homepage) return <DemoFooter customer={customer} />;

  const { footer } = (homepage?.content as any) ?? {};
  const currentYear = new Date().getFullYear();

  return (
    <>
      <SupportActions />

      {!footer?.isHidden && (
        <footer className="rounded-t-md bg-primary-900 py-15">
          <div className="container flex flex-col gap-8 justify-between px-4 mx-auto lg:flex-row">
            <div>
              <div className="mb-4">
                <Logo
                  src={
                    (customer?.logoWhite &&
                      `${FilePathUtils.customerLogoPath(customer.id, 'white')}/${customer.logoWhite}`) ??
                    ''
                  }
                  variant="white"
                  width={175}
                  height={34}
                />
              </div>
              <ul className="space-y-2 text-base font-normal text-gray-300">
                <div className="flex gap-2 items-center">
                  <HugeiconsIcon
                    icon={Location05Icon}
                    height={20}
                    width={20}
                    className="text-primary-300"
                  />
                  <li>{footer?.address?.city}</li>
                </div>
                <div className="flex gap-2 items-center">
                  <HugeiconsIcon
                    icon={CallIcon}
                    height={20}
                    width={20}
                    className="text-primary-300"
                  />
                  <li>{footer?.address?.phone}</li>
                </div>
                <div className="flex gap-2 items-center">
                  <HugeiconsIcon
                    icon={MailOpen02Icon}
                    height={20}
                    width={20}
                    className="text-primary-300"
                  />
                  <li>{footer?.address?.email}</li>
                </div>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white text-xl mb-[22px]">
                Company
              </h4>
              <ul className="grid gap-4 text-base font-normal text-gray-300">
                <li>
                  <Link href={'/about-us'}>About us</Link>
                </li>
                <li>
                  <Link href={'/terms-and-conditions'}>
                    Terms and Conditions
                  </Link>
                </li>
                <li>
                  <Link href={'/privacy-policy'}>Privacy Policy</Link>
                </li>
                <li>
                  <Link href={'/cookie-policy'}>Cookie Policy</Link>
                </li>
                <li>
                  <Link href={'/refund-policy'}>Refund policy</Link>
                </li>
                <li>
                  <Link href={'/contact-us'}>Contact us</Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white text-xl mb-[22px]">
                Resources
              </h4>
              <ul className="grid gap-4 text-base font-normal text-gray-300">
                <li>
                  <Link href="/courses">Courses</Link>
                </li>
                <li>
                  <Link href="/my-classes">My classes</Link>
                </li>
                <li>
                  <Link href="/profile">My profile</Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white text-xl mb-[22px]">
                Social Media
              </h4>
              <div className="flex justify-start mt-4 space-x-2">
                {Object.keys(footer?.socialLinks).map(key => (
                  <Link
                    href={
                      footer?.socialLinks[
                        key as keyof typeof footer.socialLinks
                      ]
                    }
                    key={key}
                    className="p-2 bg-white rounded-md"
                  >
                    {socialIconToMap[key as keyof typeof socialIconToMap]}
                  </Link>
                ))}
              </div>
              {VashaUtils.isVashaAcademy(customer.code) && (
                <div className="mt-6 text-white">
                  <ul>
                    <li>Trade Licence No:</li>
                    <li>TRAD/DNCC/016937/2025</li>
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="container mx-auto px-4  py-4 md:py-[56px]">
            <div className="w-full h-[1px] bg-primary-700"></div>
          </div>

          <div className="text-sm text-center text-gray-400">
            Copyright ©{currentYear}. All Rights Reserved.
          </div>

          {VashaUtils.isVashaAcademy(customer.code) && (
            <div className="mt-10 w-full">
              <Image
                src="/Images/ssl-banner.png"
                width={1200}
                height={20}
                alt="ssl-banner"
                className="w-full h-auto"
                priority
                quality={100}
              />
            </div>
          )}
        </footer>
      )}
    </>
  );
}
